import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { Toaster } from "react-hot-toast";

// Routes
import AdminRoute from "./routes/AdminRoute";

// Contexts
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { MetaProvider } from "./context/MetaContext";

// Admin Pages
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import ApprovalInbox from "./pages/admin/ApprovalInbox";
import AdminControlCenter from "./pages/admin/AdminControlCenter";
import AdminEmployees from "./pages/admin/Employees";
import AdminAttendance from "./pages/admin/AttendanceReport";
import HrCommand from "./pages/admin/HrCommand";
import GeneralLetters from "./pages/admin/GeneralLetters";
import SalesLiveTracking from "./pages/admin/SalesLiveTracking";
import AdminLunchAlerts from "./pages/admin/LunchAlerts";
import DepartmentEfficiency from "./pages/admin/DepartmentEfficiency";
import AdminEod from "./pages/admin/EodReport";
import AdminTasks from "./pages/admin/Tasks";

import AdminComplaints from "./pages/admin/Complaints";
import AdminOffboarding from "./pages/admin/Offboarding";
import RelievedEmployees from "./pages/admin/RelievedEmployees";
import AdminPolicies from "./pages/admin/Policies";
import WFH from "./pages/admin/WFH";
import QRCodePage from "./pages/admin/QRCodePage";
import SelfieAccessPage from "./pages/admin/SelfieAccessPage";
import AdminAnnouncements from "./pages/admin/Announcements";
import AdminLeave from "./pages/admin/AdminLeave";
import AdminLeaveSettings from "./pages/admin/AdminLeaveSettings";
import AdminGeofencing from "./pages/admin/Geofencing";
import AdminExpenses from "./pages/admin/Expenses";
import FinanceCommand from "./pages/admin/FinanceCommand";
import CompanySettings from "./pages/admin/CompanySettings";
import SalarySlipSettings from "./pages/admin/SalarySlipSettings";
import AdminEscalations from "./pages/admin/Escalations";
import AbsenceWithoutLeave from "./pages/admin/AbsenceWithoutLeave";
import AdminPayroll from "./pages/admin/Payroll";

const getEmployeePortalBase = () => {
  const configuredUrl = String(process.env.REACT_APP_EMPLOYEE_PORTAL_URL || "").trim();
  if (configuredUrl) return configuredUrl.replace(/\/+$/, "");

  if (window.location.hostname.toLowerCase().startsWith("admin.")) {
    return `${window.location.protocol}//${window.location.hostname.replace(/^admin\./i, "")}`;
  }

  return "https://screwy.info";
};

const RedirectToEmployeePortal = ({ path }) => {
  const target = `${getEmployeePortalBase()}${path}${window.location.search || ""}`;
  window.location.replace(target);
  return <div className="min-h-screen bg-[var(--bg-primary)]" />;
};

const RedirectResetPathTokenToEmployeePortal = () => {
  const token = window.location.pathname.split("/").filter(Boolean)[1] || "";
  const query = token ? `?token=${encodeURIComponent(token)}` : (window.location.search || "");
  const target = `${getEmployeePortalBase()}/reset-password${query}`;
  window.location.replace(target);
  return <div className="min-h-screen bg-[var(--bg-primary)]" />;
};

const RedirectSetPathTokenToEmployeePortal = () => {
  const token = window.location.pathname.split("/").filter(Boolean)[1] || "";
  const query = token ? `?token=${encodeURIComponent(token)}` : (window.location.search || "");
  const target = `${getEmployeePortalBase()}/set-password${query}`;
  window.location.replace(target);
  return <div className="min-h-screen bg-[var(--bg-primary)]" />;
};

function App() {
  return (
    <ThemeProvider>
      <AdminAuthProvider>
        <MetaProvider>
          <Router>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: "#1e1e1e",
                  color: "#fff",
                  border: "1px solid #2a2a2a",
                },
              }}
            />

              <Routes>
            {/* Default */}
            <Route path="/" element={<Navigate to="/admin/dashboard" />} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/set-password" element={<RedirectToEmployeePortal path="/set-password" />} />
            <Route path="/set-password/:token" element={<RedirectSetPathTokenToEmployeePortal />} />
            <Route path="/reset-password" element={<RedirectToEmployeePortal path="/reset-password" />} />
            <Route path="/reset-password/:token" element={<RedirectResetPathTokenToEmployeePortal />} />

            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/payroll-locking-system"
              element={
                <AdminRoute>
                  <AdminControlCenter />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/control-center"
              element={
                <AdminRoute>
                  <AdminControlCenter />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/approval-inbox"
              element={
                <AdminRoute>
                  <ApprovalInbox />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/employees"
              element={
                <AdminRoute>
                  <AdminEmployees />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/attendance"
              element={
                <AdminRoute>
                  <AdminAttendance />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/escalations"
              element={
                <AdminRoute>
                  <AdminEscalations />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/absence-without-leave"
              element={
                <AdminRoute>
                  <AbsenceWithoutLeave />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/hr"
              element={
                <AdminRoute>
                  <HrCommand />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/general-letters"
              element={
                <AdminRoute>
                  <GeneralLetters />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/sales-live-tracking"
              element={
                <AdminRoute>
                  <SalesLiveTracking />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/lunch-alerts"
              element={
                <AdminRoute>
                  <AdminLunchAlerts />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/department-efficiency"
              element={
                <AdminRoute>
                  <DepartmentEfficiency />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/eod"
              element={
                <AdminRoute>
                  <AdminEod />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/tasks"
              element={
                <AdminRoute>
                  <AdminTasks />
                </AdminRoute>
              }
            />



            <Route
              path="/admin/complaints"
              element={
                <AdminRoute>
                  <AdminComplaints />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/offboarding"
              element={
                <AdminRoute>
                  <AdminOffboarding />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/policies"
              element={
                <AdminRoute>
                  <AdminPolicies />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/wfh"
              element={
                <AdminRoute>
                  <WFH />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/announcements"
              element={
                <AdminRoute>
                  <AdminAnnouncements />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/leave"
              element={
                <AdminRoute>
                  <AdminLeave />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/leave-settings"
              element={
                <AdminRoute>
                  <AdminLeaveSettings />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/expenses"
              element={
                <AdminRoute>
                  <AdminExpenses />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/relieved-employees"
              element={
                <AdminRoute>
                  <RelievedEmployees />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/finance"
              element={
                <AdminRoute>
                  <FinanceCommand />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/payroll"
              element={
                <AdminRoute>
                  <AdminPayroll />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/geofencing"
              element={
                <AdminRoute>
                  <AdminGeofencing />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/salary-slip-settings"
              element={
                <AdminRoute>
                  <SalarySlipSettings />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/settings"
              element={
                <AdminRoute>
                  <CompanySettings />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/qr-code"
              element={
                <AdminRoute>
                  <QRCodePage />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/selfie-access"
              element={
                <AdminRoute>
                  <SelfieAccessPage />
                </AdminRoute>
              }
            />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/admin/dashboard" />} />
              </Routes>
          </Router>
        </MetaProvider>
      </AdminAuthProvider>
    </ThemeProvider>
  );
}

export default App;
